import { openMeetingEntry } from "utils/meetingEntry"
import React from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom";
import { checklist as checklistApi, mentorship, pdfs, images, videos } from "services/api"
import type { ChecklistItem as ChecklistItemDto } from "services/api/checklistClient"
import EventsListCard, { EventsListItem, Attachment, type EventsListCardHandle } from "components/mentorship/EventsListCard"
import {
  CohortAvatar,
  CohortEventHero,
  CohortFailureState,
  CohortHeader,
  CohortLoadingState,
  isCohortEventLive,
} from "components/mentorship/CohortExperience"
import { useAuth } from "contexts/AuthContext"
import { MilestonesTimeline, Milestone } from "components/mentorship/MilestonesTimeline"
import { RosterCard } from "components/mentorship/admin/RosterCard"
import type { Person } from "components/mentorship/admin/types"
import { ChecklistCard } from "components/mentorship/ChecklistCard"
import {
  CohortRelationshipSpotlight,
  getRelationshipFact,
  getRelationshipMeetings,
  type CohortRelationshipPerson,
} from "components/mentorship/CohortRelationshipSpotlight"
import { AppDialog } from "components/ui/app-dialog"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "components/ui/dialog"
import { Label } from "components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select"
import { Textarea } from "components/ui/textarea"
import { Button } from "components/ui/button"
import { useToast } from "components/ui/use-toast"
import { Icon } from "components/icons/Icon"
import { buildFlowPath } from "utils/flowNavigation"
import type { CohortRoleLabelSource } from "utils/cohortRoleLabels"
import { getCohortRoleLabel, getCohortRoleLabels } from "utils/cohortRoleLabels"
import { ForNextTimeDialog } from "components/mentorship/continuity/ForNextTimeDialog"
import { getContinuityCue, useMentorshipRelationships } from "components/mentorship/continuity/useMentorshipRelationships"
import { RelationshipMeetingActions } from "components/mentorship/RelationshipMeetingActions"

type ChecklistItem = ChecklistItemDto & { subtitle?: string }

type MeetingEvent = EventsListItem & {
  startTimeIso: string
  endTimeIso: string
  location: string
  description: string
  attachments: Attachment[]
}
type CohortPerson = {
  id: string
  first_name?: string
  last_name?: string
  username?: string
  email?: string
  role?: string
  membership_role?: string
  mentor_id?: string | null
  is_mentor_for_viewer?: boolean
  profile?: {
    avatar_url?: string | null
  }
  avatar_url?: string | null
}

type ChecklistUndo = {
  itemId: string
  title: string
}

const CHECKLIST_UNDO_DURATION_MS = 8_000

export const MentorshipDashboard: React.FC = () => {
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { addToast } = useToast()
  const eventsCardRef = useRef<EventsListCardHandle | null>(null)
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([])
  const [loadingChecklist, setLoadingChecklist] = useState(true)
  const [checklistError, setChecklistError] = useState<string | null>(null)
  const [showCompleted, setShowCompleted] = useState(false)
  const [savingChecklistItemIds, setSavingChecklistItemIds] = useState<Set<string>>(() => new Set())
  const [checklistRowErrors, setChecklistRowErrors] = useState<Record<string, string>>({})
  const [checklistUndo, setChecklistUndo] = useState<ChecklistUndo | null>(null)
  const checklistUndoRef = useRef<ChecklistUndo | null>(null)
  const checklistUndoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const checklistMutationQueueRef = useRef<Map<string, Promise<void>>>(new Map())
  const checklistMutationVersionRef = useRef<Map<string, number>>(new Map())

  const [events, setEvents] = useState<MeetingEvent[]>([])
  const [loadingEvents, setLoadingEvents] = useState(true)
  const [eventsError, setEventsError] = useState<string | null>(null)
  const [showPastEvents, setShowPastEvents] = useState(false)
  const [canCreateMeetings, setCanCreateMeetings] = useState(false)
  const currentUserId = currentUser?.id ? String(currentUser.id) : undefined
  const [cohortId, setCohortId] = useState<string | null>(null)
  const { relationships: accessibleContinuityRelationships, loading: continuityLoading, error: continuityError } = useMentorshipRelationships(cohortId || undefined)
  const continuityRelationships = useMemo(() => accessibleContinuityRelationships.filter(relationship =>
    String(relationship.mentor.id) === currentUserId || String(relationship.mentee.id) === currentUserId,
  ), [accessibleContinuityRelationships, currentUserId])
  const [continuityRelationshipId, setContinuityRelationshipId] = useState<string | undefined>()
  const [continuityMenteeId, setContinuityMenteeId] = useState<string | undefined>()
  const [continuityOpen, setContinuityOpen] = useState(false)
  const [cohortName, setCohortName] = useState<string | null>(null)
  const [cohortRoleLabels, setCohortRoleLabels] = useState<CohortRoleLabelSource>(null)
  const [cohortStartDate, setCohortStartDate] = useState<Date | null>(null)
  const [cohortEndDate, setCohortEndDate] = useState<Date | null>(null)
  const [milestones, setMilestones] = useState<Milestone[]>([])
  const [cohortPeople, setCohortPeople] = useState<CohortPerson[]>([])
  const [loadingPeople, setLoadingPeople] = useState(true)
  const [peopleError, setPeopleError] = useState<string | null>(null)
  const [peopleSearch, setPeopleSearch] = useState("")
  const [reportDialogOpen, setReportDialogOpen] = useState(false)
  const [reportedUserId, setReportedUserId] = useState<string>("none")
  const [reportDescription, setReportDescription] = useState("")
  const [reportSubmitting, setReportSubmitting] = useState(false)
  const [reportError, setReportError] = useState<string | null>(null)
  const [reportSubmitted, setReportSubmitted] = useState(false)
  const [reportDiscardConfirm, setReportDiscardConfirm] = useState(false)
  const [mobileView, setMobileView] = useState<"checklist" | "people" | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [dashboardLoadError, setDashboardLoadError] = useState(false)
  const [dashboardReloadKey, setDashboardReloadKey] = useState(0)
  const isDashboardLoading = loadingChecklist || loadingEvents || loadingPeople

  const meetingIdParam = useMemo(() => {
    return new URLSearchParams(location.search).get("meetingId")
  }, [location.search])

  const buildMeetingDetailsUrl = useCallback(
    (meetingId: string) => {
      const params = new URLSearchParams(location.search)
      params.set("meetingId", meetingId)
      const search = params.toString()
      return `${location.pathname}${search ? `?${search}` : ""}${location.hash}`
    },
    [location.hash, location.pathname, location.search],
  )

  const handleMeetingPopupUrlChange = useCallback(
    (meetingId: string | null) => {
      const params = new URLSearchParams(location.search)
      if (meetingId) {
        params.set("meetingId", meetingId)
      } else {
        params.delete("meetingId")
      }
      const search = params.toString()
      navigate(
        {
          pathname: location.pathname,
          search: search ? `?${search}` : "",
          hash: location.hash,
        },
        { replace: !meetingId },
      )
    },
    [location.hash, location.pathname, location.search, navigate],
  )

  const formatDateForLabel = useCallback((isoDate?: string | null) => {
    if (!isoDate) return null
    const dateOnlyMatch = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1])
      const monthIndex = Number(dateOnlyMatch[2]) - 1
      const day = Number(dateOnlyMatch[3])
      const localDate = new Date(year, monthIndex, day)
      if (Number.isNaN(localDate.getTime())) return null
      return localDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    }
    const date = new Date(isoDate)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
  }, [])

  const parseCohortDate = useCallback((isoDate?: string | null) => {
    if (!isoDate) return null
    const dateOnlyMatch = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1])
      const monthIndex = Number(dateOnlyMatch[2]) - 1
      const day = Number(dateOnlyMatch[3])
      const localDate = new Date(year, monthIndex, day)
      return Number.isNaN(localDate.getTime()) ? null : localDate
    }
    const date = new Date(isoDate)
    return Number.isNaN(date.getTime()) ? null : date
  }, [])

  const deriveSubtitle = useCallback((item: ChecklistItem) => {
    if (item.completedAt) {
      const dateLabel = formatDateForLabel(item.completedAt)
      return dateLabel ? `Completed ${dateLabel}` : "Completed"
    }
    if (item.dueDate) {
      const dateLabel = formatDateForLabel(item.dueDate)
      return dateLabel ? `Due ${dateLabel}` : "Due soon"
    }
    return ""
  }, [formatDateForLabel])

  const normalizeChecklistItem = useCallback((item: any): ChecklistItem => ({
    id: item.id,
    title: item.title,
    description: item.description ?? '',
    dueDate: item.due_date ?? null,
    completedAt: item.completed_at ?? null,
    completionSource: item.completion_source ?? null,
    externalUrl: item.external_url ?? null,
    questionFlow: item.question_flow ?? null,
    questionFlowName: item.question_flow_name ?? null,
    metadata: item.metadata ?? {},
    media: item.media
      ? {
          id: item.media.id,
          url: item.media.url ?? null,
          mediaType: item.media.media_type ?? null,
          storageKey: item.media.storage_key ?? null,
        }
      : item.media_asset
      ? {
          id: item.media_asset.id,
          url: item.media_asset.url ?? null,
          mediaType: item.media_asset.media_type ?? null,
          storageKey: item.media_asset.storage_key ?? null,
        }
      : null,
    subtitle: undefined, // populated after mapping
  }), [])

  const dismissChecklistUndo = useCallback((itemId?: string) => {
    if (itemId && checklistUndoRef.current?.itemId !== itemId) return
    if (checklistUndoTimerRef.current) {
      clearTimeout(checklistUndoTimerRef.current)
      checklistUndoTimerRef.current = null
    }
    checklistUndoRef.current = null
    setChecklistUndo(null)
  }, [])

  const offerChecklistUndo = useCallback((item: ChecklistItem) => {
    if (checklistUndoTimerRef.current) clearTimeout(checklistUndoTimerRef.current)
    const undo = { itemId: item.id, title: item.title }
    checklistUndoRef.current = undo
    setChecklistUndo(undo)
    checklistUndoTimerRef.current = setTimeout(() => {
      if (checklistUndoRef.current?.itemId === item.id) {
        checklistUndoRef.current = null
        setChecklistUndo(null)
      }
      checklistUndoTimerRef.current = null
    }, CHECKLIST_UNDO_DURATION_MS)
  }, [])

  useEffect(() => () => {
    if (checklistUndoTimerRef.current) clearTimeout(checklistUndoTimerRef.current)
  }, [])

  const updateChecklistItemCompletion = useCallback((target: ChecklistItem, shouldComplete: boolean) => {
    const itemId = target.id
    const previousItem = { ...target }
    const optimisticCompletedAt = shouldComplete ? new Date().toISOString() : null
    const mutationVersion = (checklistMutationVersionRef.current.get(itemId) ?? 0) + 1
    checklistMutationVersionRef.current.set(itemId, mutationVersion)
    setChecklistRowErrors((errors) => {
      const next = { ...errors }
      delete next[itemId]
      return next
    })
    setSavingChecklistItemIds((ids) => new Set(ids).add(itemId))
    setChecklistItems((items) => items.map((item) => item.id === itemId
      ? { ...item, completedAt: optimisticCompletedAt, subtitle: shouldComplete ? "Ticked off just now" : deriveSubtitle({ ...item, completedAt: null }) }
      : item))
    const action = shouldComplete ? checklistApi.markComplete : checklistApi.markIncomplete
    const previousMutation = checklistMutationQueueRef.current.get(itemId) ?? Promise.resolve()
    const request = previousMutation.catch(() => undefined).then(() => action(itemId))
    const serializedRequest = request.then(() => undefined, () => undefined)
    checklistMutationQueueRef.current.set(itemId, serializedRequest)

    void request
      .then((updated) => {
        if (checklistMutationVersionRef.current.get(itemId) !== mutationVersion) return
        setChecklistItems((items) =>
          items.map((item) =>
            item.id === itemId ? { ...updated, subtitle: deriveSubtitle(updated) } : item,
          ),
        )
      })
      .catch((error: any) => {
        if (checklistMutationVersionRef.current.get(itemId) !== mutationVersion) return
        setChecklistItems((items) => items.map((item) => item.id === itemId ? previousItem : item))
        setChecklistRowErrors((errors) => ({
          ...errors,
          [itemId]: error?.response?.data?.detail || error?.message || "That didn't save. The task is back to how it was.",
        }))
        if (shouldComplete) dismissChecklistUndo(itemId)
      })
      .finally(() => {
        if (checklistMutationQueueRef.current.get(itemId) === serializedRequest) {
          checklistMutationQueueRef.current.delete(itemId)
        }
        if (checklistMutationVersionRef.current.get(itemId) !== mutationVersion) return
        checklistMutationVersionRef.current.delete(itemId)
        setSavingChecklistItemIds((ids) => {
          const next = new Set(ids)
          next.delete(itemId)
          return next
        })
      })
  }, [deriveSubtitle, dismissChecklistUndo])

  const toggleChecklistItem = (itemId: string) => {
    const target = checklistItems.find((item) => item.id === itemId)
    if (!target || savingChecklistItemIds.has(itemId)) return
    if (target.questionFlowName || target.questionFlow) return
    const shouldComplete = !target.completedAt
    dismissChecklistUndo()
    if (shouldComplete) offerChecklistUndo(target)
    updateChecklistItemCompletion(target, shouldComplete)
  }

  const handleUndoChecklistItem = () => {
    const undo = checklistUndoRef.current
    if (!undo) return
    const target = checklistItems.find((item) => item.id === undo.itemId)
    dismissChecklistUndo()
    if (!target) return
    updateChecklistItemCompletion(target, false)
  }

  const resolveChecklistLink = useCallback(async (
    item: ChecklistItem,
  ): Promise<{ url: string; download: boolean; newTab: boolean } | null> => {
    if (item.externalUrl) {
      return { url: item.externalUrl, download: false, newTab: true }
    }
    const media = item.media
    if (!media) return null
    const key = media.storageKey || media.url
    if (!key) return null
    const mediaType = (media.mediaType || "").toLowerCase()
    switch (mediaType) {
      case "pdf":
        return { url: await pdfs.getSignedUrl(key), download: false, newTab: true }
      case "image":
        return { url: await images.getSignedUrl(key), download: true, newTab: true }
      case "video":
        return { url: await videos.getSignedUrl(key), download: false, newTab: true }
      default:
        return { url: media.url || key, download: true, newTab: true }
    }
  }, [])

  const handleChecklistLinkClick = async (e: React.MouseEvent, item: ChecklistItem) => {
    e.preventDefault()
    e.stopPropagation()
    setChecklistError(null)
    try {
      if (item.questionFlowName) {
        navigate(buildFlowPath({ flowName: item.questionFlowName }))
        return
      }
      const resolved = await resolveChecklistLink(item)
      if (!resolved?.url) {
        throw new Error("Unable to open link")
      }
      const link = document.createElement("a")
      link.href = resolved.url
      link.target = resolved.newTab ? "_blank" : "_self"
      link.rel = resolved.newTab ? "noopener noreferrer" : ""
      if (resolved.download) {
        link.download = item.title
      }
      link.click()
    } catch (err: any) {
      setChecklistError(err?.response?.data?.detail || err?.message || "Unable to open link")
    }
  }

  const normalizedPeople = useMemo((): Person[] => {
    const roleLabel = (role?: string) => {
      const normalized = (role || "mentee").toLowerCase().replace(/\s+/g, "_")
      return getCohortRoleLabel(normalized, cohortRoleLabels)
    }
    return cohortPeople.map((person) => {
      const roleKey = (person.membership_role || person.role || "mentee").toLowerCase().replace(/\s+/g, "_")
      const nameParts = [person.first_name, person.last_name].filter(Boolean)
      const displayName =
        nameParts.join(" ").trim() ||
        person.username ||
        person.email ||
        "Unknown member"
      const initials =
        nameParts
          .map((p) => p?.[0] || "")
          .join("")
          .trim()
          .toUpperCase() ||
        (displayName[0]?.toUpperCase() ?? "U")

      return {
        id: Number(person.id),
        name: displayName,
        initials,
        avatarUrl: person.profile?.avatar_url ?? person.avatar_url ?? null,
        role: roleLabel(roleKey),
        roleKey: roleKey as Person["roleKey"],
        isMyMentor: Boolean(person.is_mentor_for_viewer),
        isMyMentee:
          Boolean(currentUser?.id) &&
          String(person.mentor_id || "") === String(currentUser?.id) &&
          String(person.id) !== String(currentUser?.id),
        status: "on-track",
        adminNote: "",
        recentEvents: [],
        openFlags: [],
        openTodos: [],
        taskCompletion: {
          completed: 0,
          total: 0,
        },
      }
    })
  }, [cohortPeople, cohortRoleLabels, currentUser?.id])

  const relationshipPeople = useMemo((): CohortRelationshipPerson[] => {
    const emailById = new Map(cohortPeople.map((person) => [String(person.id), person.email || null]))
    return normalizedPeople.map((person) => ({
      id: person.id,
      name: person.name,
      initials: person.initials,
      avatarUrl: person.avatarUrl,
      email: emailById.get(String(person.id)) || null,
    }))
  }, [cohortPeople, normalizedPeople])

  const relationshipMentor = useMemo(() => {
    const mentor = normalizedPeople.find((person) => person.isMyMentor)
    return mentor ? relationshipPeople.find((person) => String(person.id) === String(mentor.id)) || null : null
  }, [normalizedPeople, relationshipPeople])

  const relationshipMentees = useMemo(() => {
    const menteeIds = new Set(normalizedPeople.filter((person) => person.isMyMentee).map((person) => String(person.id)))
    return relationshipPeople.filter((person) => menteeIds.has(String(person.id)))
  }, [normalizedPeople, relationshipPeople])

  const continuityByPerson = useMemo(() => {
    const rows: Record<string, (typeof continuityRelationships)[number]> = {}
    continuityRelationships.filter(relationship => relationship.is_current).forEach(relationship => {
      const person = String(relationship.mentor.id) === currentUserId ? relationship.mentee : relationship.mentor
      rows[String(person.id)] = relationship
    })
    return rows
  }, [continuityRelationships, currentUserId])
  const continuityCues = useMemo(() => {
    const assignedPeople = [...relationshipMentees, ...(relationshipMentor ? [relationshipMentor] : [])]
    return Object.fromEntries(assignedPeople.map(person => {
      const relationship = continuityByPerson[String(person.id)]
      return [String(person.id), relationship ? getContinuityCue(relationship) : continuityError ? 'Notes unavailable · Try again' : 'For next time']
    }))
  }, [continuityByPerson, continuityError, relationshipMentees, relationshipMentor])
  const openContinuity = (relationshipId: string) => {
    setContinuityRelationshipId(relationshipId)
    setContinuityMenteeId(undefined)
    setContinuityOpen(true)
  }
  // `?record=<relationship>` or `?mentee=<id>` (post-call card, recap-ready notifications) opens the
  // record once and then leaves a clean URL behind so a refresh does not reopen it.
  useEffect(() => {
    if (isDashboardLoading) return
    const params = new URLSearchParams(location.search)
    const record = params.get("record")
    const mentee = params.get("mentee")
    const nextTime = params.get("nexttime")
    if (!record && !mentee && !nextTime) return
    // `?nexttime=1` (a mentor leaving a group session) waits for the records, then opens the first current one.
    const firstCurrent = continuityRelationships.find(relationship => relationship.is_current)
    if (cohortId && nextTime && !record && !mentee && !firstCurrent && continuityLoading) return
    if (cohortId && (record || mentee || firstCurrent)) {
      setContinuityRelationshipId(record || (nextTime && !mentee ? firstCurrent?.id : undefined))
      setContinuityMenteeId(record || nextTime ? undefined : mentee || undefined)
      setContinuityOpen(true)
    }
    // An unavailable relationship must also release the intent that pauses Daily Pulse.
    params.delete("record")
    params.delete("mentee")
    params.delete("nexttime")
    const search = params.toString()
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true })
  }, [cohortId, continuityLoading, continuityRelationships, isDashboardLoading, location.pathname, location.search, navigate])
  const openPersonContinuity = (person: CohortRelationshipPerson) => {
    const relationship = continuityByPerson[String(person.id)]
    if (relationship) openContinuity(relationship.id)
    else {
      setContinuityRelationshipId(undefined)
      setContinuityMenteeId(relationshipMentees.some(mentee => String(mentee.id) === String(person.id)) ? String(person.id) : currentUserId)
      setContinuityOpen(true)
    }
  }

  const configuredRelationshipLabels = useMemo(
    () => getCohortRoleLabels(cohortRoleLabels),
    [cohortRoleLabels],
  )

  const relationshipMentorMeetingFact = useMemo(() => {
    if (!relationshipMentor) return "Matching in progress"
    const { next, last } = getRelationshipMeetings(events, cohortId || undefined, currentUserId, relationshipMentor.id)
    return getRelationshipFact(next, last, currentUser?.timezone)
  }, [cohortId, currentUserId, currentUser?.timezone, events, relationshipMentor])

  const relationshipMenteeRows = useMemo(() => relationshipMentees.map((person) => {
    const { next, last } = getRelationshipMeetings(events, cohortId || undefined, currentUserId, person.id)
    return { person, fact: getRelationshipFact(next, last, currentUser?.timezone) }
  }), [cohortId, currentUserId, currentUser?.timezone, events, relationshipMentees])

  const relationshipFacts = useMemo(() => {
    const facts: Record<string, string> = {}
    if (relationshipMentor) facts[String(relationshipMentor.id)] = relationshipMentorMeetingFact
    relationshipMenteeRows.forEach(({ person, fact }) => { facts[String(person.id)] = fact })
    return facts
  }, [relationshipMenteeRows, relationshipMentor, relationshipMentorMeetingFact])

  const menteeIds = useMemo(() => new Set(relationshipMentees.map((person) => String(person.id))), [relationshipMentees])
  const peoplePreview = useMemo(() => normalizedPeople.filter((person) => {
    if (String(person.id) === String(currentUserId || "")) return false
    if (!canCreateMeetings && person.isMyMentor) return false
    if (canCreateMeetings && menteeIds.has(String(person.id))) return false
    return true
  }).slice(0, 2), [canCreateMeetings, currentUserId, menteeIds, normalizedPeople])

  const handleScheduleMenteeTime = useCallback((person: CohortRelationshipPerson) => {
    eventsCardRef.current?.openCreateEvent({
      title: `1:1 with ${person.name}`,
      inviteesMode: "custom",
      invitedUserIds: [person.id],
    })
  }, [])

  const filteredPeople = useMemo(() => {
    const query = peopleSearch.trim().toLowerCase()
    if (!query) return normalizedPeople

    return normalizedPeople.filter((person) => {
      const nameMatch = person.name.toLowerCase().includes(query)
      const roleMatch = person.role?.toLowerCase().includes(query)
      return nameMatch || roleMatch
    })
  }, [normalizedPeople, peopleSearch])

  const reportablePeople = useMemo(
    () => [...normalizedPeople].sort((a, b) => a.name.localeCompare(b.name)),
    [normalizedPeople],
  )

  const reporterName = useMemo(() => {
    const full = `${currentUser?.first_name || ""} ${currentUser?.last_name || ""}`.trim()
    return full || currentUser?.username || "Current user"
  }, [currentUser?.first_name, currentUser?.last_name, currentUser?.username])
  const meetingRoster = useMemo(() => {
    const members: Array<{ id: string | number; name: string; role?: string; roleKey?: Person["roleKey"]; avatarUrl?: string | null }> = normalizedPeople.map((person) => ({
      id: person.id,
      name: person.name,
      role: person.role,
      roleKey: person.roleKey,
      avatarUrl: person.avatarUrl,
    }))
    if (currentUser && !members.some((person) => String(person.id) === String(currentUser.id))) {
      members.push({
        id: currentUser.id,
        name: reporterName,
        role: currentUser.role || undefined,
        avatarUrl: currentUser.avatar_url ?? null,
      })
    }
    return members
  }, [currentUser, normalizedPeople, reporterName])

  const featuredEvent = useMemo(() => {
    const now = Date.now()
    return [...events]
      .filter((event) => {
        if (event.canJoinVideo) return true
        const end = event.endTimeIso ? new Date(event.endTimeIso).getTime() : Number.POSITIVE_INFINITY
        return event.status !== "past" && end >= now
      })
      .sort((first, second) => {
        if (first.canJoinVideo !== second.canJoinVideo) return first.canJoinVideo ? -1 : 1
        return new Date(first.startTimeIso).getTime() - new Date(second.startTimeIso).getTime()
      })[0] ?? null
  }, [events])

  const openMeetingRoom = useCallback((event: EventsListItem) => {
    const target = event.guestAccessEnabled && event.guestJoinUrl
      ? event.guestJoinUrl
      : `/meetings/${event.id}`
    openMeetingEntry({ meetingId: event.id, guestJoinUrl: event.guestAccessEnabled ? event.guestJoinUrl : null, navigate, windowTarget: target })
  }, [navigate])

  const resetReportDialog = () => {
    setReportedUserId("none")
    setReportDescription("")
    setReportError(null)
    setReportSubmitting(false)
    setReportSubmitted(false)
    setReportDiscardConfirm(false)
  }

  const handleOpenReportDialog = (personId?: string | number) => {
    resetReportDialog()
    if (personId !== undefined) setReportedUserId(String(personId))
    setReportDialogOpen(true)
  }

  const handleOpenRelationshipPeople = () => {
    if (window.innerWidth < 1024) {
      setMobileView("people")
      return
    }
    const roster = document.getElementById("cohort-roster-heading")
    roster?.scrollIntoView?.({ behavior: "smooth", block: "start" })
    window.setTimeout(() => document.getElementById("cohort-roster-heading")?.focus?.({ preventScroll: true }), 350)
  }

  const handleMobileMenuSelection = (view: "schedule" | "checklist" | "people") => {
    setMobileMenuOpen(false)
    if (view === "schedule") {
      document.getElementById("cohort-schedule-heading")?.scrollIntoView?.({ behavior: "smooth", block: "start" })
      window.setTimeout(() => document.getElementById("cohort-schedule-heading")?.focus?.({ preventScroll: true }), 350)
      return
    }
    setMobileView(view)
  }

  const handleCloseReportDialog = () => {
    if (reportSubmitting) return
    if (!reportSubmitted && reportDescription.trim().length >= 20 && !reportDiscardConfirm) {
      setReportDiscardConfirm(true)
      return
    }
    setReportDialogOpen(false)
    resetReportDialog()
  }

  const handleSubmitSafetyReport = async () => {
    const description = reportDescription.trim()
    if (!description || reportSubmitting) return
    setReportSubmitting(true)
    setReportError(null)
    try {
      const parsedUserId = reportedUserId === "none" ? null : Number(reportedUserId)
      const payload =
        parsedUserId === null || Number.isNaN(parsedUserId)
          ? { description, reported_user_id: null }
          : { description, reported_user_id: parsedUserId }
      await mentorship.reportSafetySignal(payload)
      setReportSubmitted(true)
      addToast({ title: "Report submitted", description: "An administrator will review it." })
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || "Unable to submit report"
      setReportError(message)
      addToast({ title: "Error", description: message, variant: "destructive" })
    } finally {
      setReportSubmitting(false)
    }
  }

  const rosterEmptyState = peopleError ? (
    <p className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2">
      {peopleError}
    </p>
  ) : loadingPeople ? (
    <p className="text-sm text-muted-foreground">Loading people...</p>
  ) : normalizedPeople.length === 0 ? (
    <p className="text-sm text-muted-foreground">No people found in your cohort yet.</p>
  ) : filteredPeople.length === 0 ? (
    <p className="text-sm text-muted-foreground">No matches for "{peopleSearch.trim()}"</p>
  ) : null

  const rosterForCard = peopleError || loadingPeople ? [] : filteredPeople

  const handleSelectRosterPerson = (person: Person) => {
    navigate(`/profile/${person.id}`)
  }

  const formatDuration = useCallback((start: Date, end: Date) => {
    const minutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000))
    if (minutes === 0) return "0 min"
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    if (hours > 0 && remainingMinutes > 0) return `${hours}h ${remainingMinutes}m`
    if (hours > 0) return `${hours}h`
    return `${remainingMinutes} min`
  }, [])

  const getLocalTimeZoneAbbr = useCallback((date: Date) => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZoneName: "short",
    }).formatToParts(date)
    return parts.find((p) => p.type === "timeZoneName")?.value || Intl.DateTimeFormat().resolvedOptions().timeZone
  }, [])

  const toMeetingEvent = useCallback((meeting: any): MeetingEvent => {
    const start = new Date(meeting.start_time)
    const end = new Date(meeting.end_time)
    const canJoinVideo = Boolean(meeting?.can_join_video)
    const status: "upcoming" | "past" = canJoinVideo
      ? "upcoming"
      : meeting?.status === "past" || end.getTime() < Date.now()
        ? "past"
        : "upcoming"
    const startTime = start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    const endTime = end.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    const timeRange = `${startTime} - ${endTime}`
    const timeZoneAbbr = getLocalTimeZoneAbbr(start)
    const dateLabel = start.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })

    const displayNameFromKey = (original?: string | null, key?: string | null, fallback?: string) => {
      const trimmed = original?.trim()
      if (trimmed) return trimmed
      const source = key || ""
      if (source) {
        const parts = source.split(/[/\\]/).filter(Boolean)
        if (parts.length) return parts[parts.length - 1]
      }
      return fallback || "Attachment"
    }

    const invitedUserIds = Array.isArray(meeting.invited_user_ids)
      ? meeting.invited_user_ids.map((id: string | number) => String(id))
      : []
    const explicitMode =
      meeting.invitees_mode === "cohort" ||
      meeting.invitees_mode === "mentors" ||
      meeting.invitees_mode === "scholars" ||
      meeting.invitees_mode === "custom"
        ? meeting.invitees_mode
        : undefined
    const inviteesMode = explicitMode
      ? explicitMode
      : invitedUserIds.includes("__COHORT__")
        ? "cohort"
        : invitedUserIds.includes("__MENTORS__")
          ? "mentors"
          : invitedUserIds.includes("__SCHOLARS__")
            ? "scholars"
            : "custom"
    const recurrence = meeting.recurrence
      ? {
          interval: meeting.recurrence.interval,
          period: meeting.recurrence.period,
          daysOfWeek: meeting.recurrence.days_of_week || meeting.recurrence.daysOfWeek,
          startDate: meeting.recurrence.start_date || meeting.recurrence.startDate,
          endDate: meeting.recurrence.end_date || meeting.recurrence.endDate || undefined,
          noEndDate: Boolean(meeting.recurrence.no_end_date || meeting.recurrence.noEndDate),
          materializedUntil: meeting.recurrence.materialized_until || meeting.recurrence.materializedUntil || undefined,
        }
      : null

      return {
        id: String(meeting.id),
        title: meeting.title || "Meeting",
        startTimeIso: meeting.start_time,
        endTimeIso: meeting.end_time,
        timeZone: meeting.time_zone,
        timeZoneAbbr,
        date: dateLabel,
        time: timeRange,
        duration: formatDuration(start, end),
        location: meeting.location || "",
        cohortId: meeting.cohort_id ? String(meeting.cohort_id) : null,
        isVirtual: Boolean(meeting.is_virtual),
        guestAccessEnabled: Boolean(meeting.guest_access_enabled),
        waitingRoomEnabled: Boolean(meeting.waiting_room_enabled),
        guestJoinUrl: meeting.guest_join_url || null,
        canJoinVideo,
        activeParticipantCount: Number(meeting.active_participant_count || 0),
        lateJoinUntil: meeting.late_join_until || null,
        manuallyEndedAt: meeting.manually_ended_at || null,
        questionCollectionEnabled: Boolean(meeting.question_collection_enabled),
        questionSubmissionCount: Number(meeting.question_submission_count || 0),
        unreviewedQuestionSubmissionCount: Number(meeting.unreviewed_question_submission_count || 0),
        virtualLink: meeting.video_url || undefined,
        description: meeting.description || "",
        attendanceTracked: Boolean(meeting.attendance_tracked),
        summaryText: (meeting as any).summary_text,
        transcriptionStatus: (meeting as any).transcription_status,
        transcriptText: (meeting as any).transcript_text || null,
        recordingEnabled: Boolean((meeting as any).recording_enabled),
        recordingStatus: (meeting as any).recording_status || null,
        recordingAvailable: Boolean((meeting as any).recording_available),
        createdById: meeting.host?.id ? String(meeting.host.id) : meeting.host_user ? String(meeting.host_user) : undefined,
        attachments: Array.isArray(meeting.resources)
        ? meeting.resources
            .filter((r: any) => r?.storage_key)
            .map((r: any) => ({
              key: r.storage_key as string,
              name: displayNameFromKey(r.original_filename, r.storage_key, r.storage_key),
              mime: r.media_type,
              originalFilename: r.original_filename,
              assetId: r.media_asset ? String(r.media_asset) : undefined,
            }))
        : [],
      invitedUserIds,
      inviteesMode,
      status,
      isMilestone: Boolean(meeting.is_milestone),
      recurrence,
      recurrenceSeriesId: meeting.series_id || meeting.recurrence?.series_id || null,
      isException: Boolean(meeting.is_exception),
      occurrenceStatus: meeting.occurrence_status || (meeting.status === "cancelled" ? "cancelled" : meeting.is_exception ? "edited" : "normal"),
    }
  }, [formatDuration, getLocalTimeZoneAbbr])

  useEffect(() => {
    let isMounted = true

    const normalizeError = (err: any) => {
      if (!err) return null
      if (typeof err === "string") return err
      if (typeof err?.detail === "string") return err.detail
      if (typeof err?.message === "string") return err.message
      return "Unable to load data"
    }

    const loadDashboard = async () => {
      setLoadingPeople(true)
      setLoadingChecklist(true)
      setLoadingEvents(true)
      setPeopleError(null)
      setChecklistError(null)
      setEventsError(null)
      setDashboardLoadError(false)
      try {
        const data = await mentorship.getDashboard()
        if (!isMounted) return
        setCohortId(data.cohort?.id ? String(data.cohort.id) : null)
        setCohortName(data.cohort?.name || null)
        setCohortRoleLabels(data.cohort || null)
        setCohortStartDate(parseCohortDate(data.cohort?.start_date))
        setCohortEndDate(parseCohortDate(data.cohort?.end_date))
        setCohortPeople(Array.isArray(data.members) ? data.members : [])
        const checklist = Array.isArray(data.checklist_items)
          ? data.checklist_items.map((item) => normalizeChecklistItem(item))
          : []
        setChecklistItems(checklist.map((item) => ({ ...item, subtitle: deriveSubtitle(item) })))
        const meetingList = (Array.isArray(data.meetings)
          ? data.meetings
          : Array.isArray((data as any)?.results)
            ? (data as any).results
            : [])
          .filter((meeting: any) => meeting?.status !== "cancelled" && meeting?.occurrence_status !== "cancelled")
        const milestoneSource: Milestone[] = meetingList
          .filter((meeting: any) => Boolean(meeting?.is_milestone))
          .map((meeting: any) => ({
            id: String(meeting.id),
            name: meeting.title || "Milestone",
            date: meeting.start_time,
          }))
          .sort((a: Milestone, b: Milestone) => new Date(a.date).getTime() - new Date(b.date).getTime())
        setMilestones(milestoneSource)
        setEvents(meetingList.map(toMeetingEvent))

        const cohortRole = (data.cohort?.role || "").toLowerCase()
        const userIsMentor = (currentUser?.role || "").toLowerCase() === "mentor"
        setCanCreateMeetings(userIsMentor || cohortRole === "mentor")

        setPeopleError(normalizeError(data.errors?.members))
        setChecklistError(normalizeError(data.errors?.checklist))
        setEventsError(normalizeError(data.errors?.meetings))
      } catch (e: any) {
        if (!isMounted) return
        setDashboardLoadError(true)
        setPeopleError(null)
        setChecklistError(null)
        setEventsError(null)
        setChecklistItems([])
        setEvents([])
        setCohortPeople([])
        setCohortId(null)
        setCohortRoleLabels(null)
        setCohortStartDate(null)
        setCohortEndDate(null)
        setMilestones([])
      } finally {
        if (!isMounted) return
        setLoadingPeople(false)
        setLoadingChecklist(false)
        setLoadingEvents(false)
      }
    }

    loadDashboard()
    return () => {
      isMounted = false
    }
  }, [currentUser?.role, dashboardReloadKey, deriveSubtitle, normalizeChecklistItem, parseCohortDate, toMeetingEvent])

  if (isDashboardLoading) return <CohortLoadingState />

  if (dashboardLoadError) {
    return <CohortFailureState onRetry={() => setDashboardReloadKey((key) => key + 1)} />
  }

  const mobileViewTitle = mobileView === "checklist" ? "Checklist" : "Your people"

  return (
    <div className="cohort-experience cohort-page-surface">
      <div className="cohort-member-layout relative mx-auto max-w-6xl space-y-6">
        <div>
          <CohortHeader
            name={cohortName}
            startDate={cohortStartDate}
            endDate={cohortEndDate}
            peopleCount={normalizedPeople.length}
            people={normalizedPeople}
            onOpenMenu={() => setMobileMenuOpen((open) => !open)}
          />
        </div>

        {mobileMenuOpen ? (
          <div className="absolute right-0 top-14 z-20 w-52 rounded-2xl border border-[var(--cohort-rule)] bg-[var(--cohort-paper)] p-2 shadow-xl lg:hidden">
            {(["schedule", "checklist", "people"] as const).map((view) => (
              <button key={view} type="button" onClick={() => handleMobileMenuSelection(view)} className="flex min-h-11 w-full items-center rounded-xl px-3 text-left text-sm capitalize hover:bg-black/5">{view}</button>
            ))}
          </div>
        ) : null}

        <div className="hidden lg:block">
          <MilestonesTimeline startDate={cohortStartDate} endDate={cohortEndDate} milestones={milestones} showMonthTicks={false} />
        </div>

        <CohortEventHero
          viewerTimeZone={currentUser?.timezone}
          event={featuredEvent}
          canCreate={canCreateMeetings}
          roster={normalizedPeople}
          onOpenEvent={(event) => eventsCardRef.current?.openEventDetails(event.id)}
          onCreateEvent={() => eventsCardRef.current?.openCreateEvent()}
          onJoinEvent={openMeetingRoom}
          deferLiveActionOnMobile
        />

          <div className="cohort-member-dashboard-grid grid grid-cols-1 gap-x-12 gap-y-6 lg:grid-cols-12">
            <div className="cohort-member-dashboard-rail cohort-member-dashboard-rail--right contents lg:order-2 lg:col-span-5 lg:flex lg:flex-col lg:gap-9">
            <CohortRelationshipSpotlight
              viewerTimeZone={currentUser?.timezone}
              display="desktop"
              viewerIsMentor={canCreateMeetings}
              cohortId={cohortId || undefined}
              currentUserId={currentUserId}
              mentor={relationshipMentor}
              mentees={relationshipMentees}
              continuityCues={continuityCues}
              onOpenContinuity={openPersonContinuity}
              events={events}
              mentorLabel={configuredRelationshipLabels.mentorSingular}
              menteeLabelPlural={configuredRelationshipLabels.menteePlural}
              onScheduleTime={canCreateMeetings ? handleScheduleMenteeTime : undefined}
              onJoinMeeting={openMeetingRoom}
              onOpenPeople={handleOpenRelationshipPeople}
              onReportConcern={(person) => handleOpenReportDialog(person.id)}
              onViewProfile={(person) => navigate(`/profile/${person.id}`)}
            />
            {continuityRelationships.some(relationship => !relationship.is_current) ? <button type="button" onClick={() => openContinuity(continuityRelationships.find(relationship => !relationship.is_current)!.id)} className="cohort-text-action hidden min-h-11 text-left text-sm lg:block">Past relationship notes</button> : null}
            <div className="order-4 space-y-5">
              <div className="hidden rounded-[14px] border border-[var(--cohort-rule)] bg-white p-5 sm:block">
                <div className="flex items-start gap-3">
                  <Icon name="caution" className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[var(--cohort-ink)]">Something feel wrong?</p>
                    <p className="mt-1 text-xs leading-5 text-[var(--cohort-ink-muted)]">Send a private note to your cohort administrators. Nobody else sees it.</p>
                  </div>
                </div>
                <Button type="button" variant="outline" onClick={() => handleOpenReportDialog()} className="cohort-secondary-button mt-3 w-full !border-[var(--cohort-action)] !text-[var(--cohort-orange-ink)]">Report a concern</Button>
              </div>
            </div>
          </div>

          <div className="cohort-member-dashboard-rail cohort-member-dashboard-rail--left contents lg:order-1 lg:col-span-7 lg:flex lg:flex-col lg:gap-9">
            <EventsListCard
              ref={eventsCardRef}
              title="Schedule"
              events={events}
              loading={loadingEvents}
              error={eventsError}
              showPast={showPastEvents}
              onTogglePast={(checked) => setShowPastEvents(!!checked)}
              isAdmin={false}
              canCreateEvent={canCreateMeetings}
              currentUserId={currentUserId}
              cohortId={cohortId}
              roster={meetingRoster}
              roleLabels={cohortRoleLabels}
              deepLinkedMeetingId={meetingIdParam}
              buildMeetingDetailsUrl={buildMeetingDetailsUrl}
              onMeetingPopupUrlChange={handleMeetingPopupUrlChange}
              onEventsChange={(nextEvents) => setEvents(nextEvents as MeetingEvent[])}
              continuityRelationships={continuityRelationships}
              onOpenContinuity={openContinuity}
              className="order-1"
            />
            <ChecklistCard
              items={checklistItems}
              loading={loadingChecklist}
              error={checklistError}
              showCompleted={showCompleted}
              onToggleShowCompleted={setShowCompleted}
              onToggleItem={toggleChecklistItem}
              onLinkClick={handleChecklistLinkClick}
              getSubtitle={deriveSubtitle}
              savingItemIds={savingChecklistItemIds}
              rowErrors={checklistRowErrors}
              onRetryItem={toggleChecklistItem}
              recentlyCompletedItemId={checklistUndo?.itemId}
              className="order-2"
            />

            <section className="cohort-member-people-mobile cohort-member-band cohort-member-band--sand order-3 min-w-0 lg:hidden" aria-labelledby="cohort-mobile-people-heading">
              <p className="cohort-section-eyebrow">Your people</p>
              <div className="flex items-baseline justify-between gap-3">
                <h2 id="cohort-mobile-people-heading" className="text-[21px]">Who&apos;s in this cohort</h2>
                <button type="button" onClick={() => setMobileView("people")} className="cohort-text-action min-h-11 text-sm">See all {rosterForCard.length}</button>
              </div>

              {!canCreateMeetings ? (
                relationshipMentor ? (
                  <CohortRelationshipSpotlight
                    viewerTimeZone={currentUser?.timezone}
                    display="mobile"
                    viewerIsMentor={false}
                    cohortId={cohortId || undefined}
                    currentUserId={currentUserId}
                    mentor={relationshipMentor}
                    continuityCues={continuityCues}
                    onOpenContinuity={openPersonContinuity}
                    events={events}
                    mentorLabel={configuredRelationshipLabels.mentorSingular}
                    menteeLabelPlural={configuredRelationshipLabels.menteePlural}
                    onJoinMeeting={openMeetingRoom}
                    onOpenPeople={handleOpenRelationshipPeople}
                    onReportConcern={(person) => handleOpenReportDialog(person.id)}
                    onViewProfile={(person) => navigate(`/profile/${person.id}`)}
                    mobileTrigger={(
                      <button type="button" className="cohort-mentor-pinned-card cohort-row-action flex min-h-16 w-full items-center gap-[13px] p-3.5 text-left lg:hidden">
                        <CohortAvatar person={relationshipMentor} size="relationship" tone="mentor" />
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs uppercase tracking-[0.04em] text-[var(--cohort-orange-ink)]">Your {configuredRelationshipLabels.mentorSingular}</span>
                          <span className="mt-0.5 block break-words text-[17px] font-medium">{relationshipMentor.name}</span>
                          <span className="mt-0.5 block break-words text-sm text-[var(--cohort-ink-muted)]">{relationshipMentorMeetingFact}</span>
                          {continuityCues[String(relationshipMentor.id)] ? <span className="mt-1 block text-xs text-[var(--cohort-forest)]">{continuityCues[String(relationshipMentor.id)]}</span> : null}
                        </span>
                        <span className="shrink-0 text-[22px] text-[var(--cohort-ink-subtle)]" aria-hidden="true">›</span>
                      </button>
                    )}
                  />
                ) : (
                  <div className="cohort-mentor-pinned-card mt-2.5 flex min-h-16 items-center gap-[13px] p-3.5">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-dashed border-[var(--cohort-rule-strong)] text-sm">—</span>
                    <span><span className="block text-xs uppercase tracking-[0.04em] text-[var(--cohort-orange-ink)]">Your {configuredRelationshipLabels.mentorSingular}</span><span className="mt-0.5 block text-[17px] font-medium">Pending match</span></span>
                  </div>
                )
              ) : (
                <div className="mt-3.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="text-xs uppercase tracking-[0.06em] text-[var(--cohort-orange-ink)]">Your {configuredRelationshipLabels.menteePlural} · {relationshipMenteeRows.length}</p>
                    <button type="button" onClick={() => setMobileView("people")} className="cohort-text-action min-h-11 text-sm">See all {relationshipMenteeRows.length}</button>
                  </div>
                  {relationshipMenteeRows.slice(0, 3).map(({ person, fact }) => (
                    <button key={person.id} type="button" onClick={() => openPersonContinuity(person)} className="cohort-ruled-row cohort-row-action flex min-h-[60px] w-full items-center gap-[13px] py-[11px] text-left">
                      <CohortAvatar person={person} size="row" />
                      <span className="min-w-0 flex-1"><span className="block break-words text-base font-medium">{person.name}</span><span className="mt-0.5 block break-words text-sm text-[var(--cohort-ink-muted)]">{fact}</span>{continuityCues[String(person.id)] ? <span className="mt-1 block text-xs text-[var(--cohort-forest)]">{continuityCues[String(person.id)]}</span> : null}</span>
                      <span className="shrink-0 text-xl text-[var(--cohort-ink-subtle)]" aria-hidden="true">›</span>
                    </button>
                  ))}
                  {relationshipMenteeRows.length === 0 ? <p className="py-4 text-sm text-[var(--cohort-ink-muted)]">No {configuredRelationshipLabels.menteePlural.toLowerCase()} are assigned yet.</p> : null}
                  {peoplePreview.length ? <p className="mt-4 text-xs uppercase tracking-[0.06em] text-[var(--cohort-ink-subtle)]">Everyone else</p> : null}
                </div>
              )}

              {continuityRelationships.some(relationship => !relationship.is_current) ? <button type="button" onClick={() => openContinuity(continuityRelationships.find(relationship => !relationship.is_current)!.id)} className="cohort-text-action min-h-11 text-left text-sm">Past relationship notes</button> : null}
              {peoplePreview.map((person) => (
                <button key={person.id} type="button" onClick={() => handleSelectRosterPerson(person)} className="cohort-ruled-row cohort-row-action flex min-h-[58px] w-full items-center gap-[13px] py-[11px] text-left">
                  <CohortAvatar person={person} size="row" tone={person.roleKey === "mentor" ? "mentor" : "default"} />
                  <span className="min-w-0 flex-1"><span className="block break-words text-base font-medium">{person.name}</span><span className="mt-0.5 block break-words text-sm text-[var(--cohort-ink-muted)]">{person.role}</span></span>
                </button>
              ))}
            </section>

            <RosterCard
              title="Your people"
              roster={rosterForCard}
              searchValue={peopleSearch}
              onSearchChange={setPeopleSearch}
              onSelectPerson={handleSelectRosterPerson}
              emptyState={rosterEmptyState}
              previewLimit={6}
              onOpenAll={() => setMobileView("people")}
              relationshipPriority={canCreateMeetings ? "mentees" : "mentor"}
              relationshipFacts={relationshipFacts}
              className="cohort-member-people-desktop order-3 hidden lg:block"
            />
          </div>
        </div>
        <button type="button" onClick={() => handleOpenReportDialog()} className={`cohort-member-safety-row cohort-mobile-bleed cohort-row-action flex min-h-[58px] w-[calc(100%+2.5rem)] items-center gap-3 px-5 text-left text-sm text-[var(--cohort-ink-muted)] sm:hidden ${canCreateMeetings ? "bg-[var(--cohort-band-sand)]" : "bg-white"}`}>
          <Icon name="caution" className="h-[17px] w-[17px] shrink-0" />
          <span className="flex-1">Report a concern privately</span>
          <span aria-hidden="true">›</span>
        </button>
      </div>

      {featuredEvent?.canJoinVideo && isCohortEventLive(featuredEvent) && !mobileView ? (
        <div className="fixed inset-x-0 bottom-[calc(3.75rem+env(safe-area-inset-bottom))] z-30 border-t border-[var(--cohort-rule)] bg-[rgba(248,247,243,0.96)] px-4 py-3 backdrop-blur sm:hidden">
          <Button type="button" onClick={() => openMeetingRoom(featuredEvent)} className="cohort-primary-button min-h-[52px] w-full text-[17px]">Join meeting</Button>
        </div>
      ) : null}

      {mobileView ? (
        <Dialog open onOpenChange={(open) => { if (!open) setMobileView(null) }}>
        <DialogContent hideCloseButton overlayClassName="cohort-expanded-panel-overlay" className="cohort-mobile-panel cohort-experience">
          <div className="cohort-expanded-panel__header sticky top-0 z-10 flex min-h-16 items-center gap-3 border-b border-[var(--cohort-rule)] bg-[var(--cohort-paper)] px-4 lg:px-6">
            <button type="button" onClick={() => setMobileView(null)} className="flex h-11 w-11 items-center justify-center rounded-xl hover:bg-black/5 lg:hidden" aria-label={`Back from ${mobileViewTitle}`}>←</button>
            <DialogTitle className="text-xl font-normal">{mobileViewTitle}</DialogTitle>
            <DialogDescription className="sr-only">Expanded {mobileViewTitle.toLowerCase()} view.</DialogDescription>
            <button type="button" onClick={() => setMobileView(null)} className="cohort-icon-button ml-auto hidden h-11 w-11 items-center justify-center text-[var(--cohort-ink-muted)] lg:flex" aria-label={`Close ${mobileViewTitle}`}>
              <span aria-hidden="true" className="text-[24px] leading-none">×</span>
            </button>
          </div>
          <div data-testid="cohort-expanded-panel-body" className="cohort-expanded-panel__body px-4 pb-8 pt-3 lg:px-6 lg:pb-6 lg:pt-4">
            {mobileView === "checklist" ? (
              <ChecklistCard items={checklistItems} loading={loadingChecklist} error={checklistError} showCompleted={showCompleted} onToggleShowCompleted={setShowCompleted} onToggleItem={toggleChecklistItem} onLinkClick={handleChecklistLinkClick} getSubtitle={deriveSubtitle} title="Checklist" savingItemIds={savingChecklistItemIds} rowErrors={checklistRowErrors} onRetryItem={toggleChecklistItem} recentlyCompletedItemId={checklistUndo?.itemId} />
            ) : null}
            {mobileView === "people" ? (
              <RosterCard title="Your people" roster={rosterForCard} searchValue={peopleSearch} onSearchChange={setPeopleSearch} onSelectPerson={handleSelectRosterPerson} emptyState={rosterEmptyState} groupByRole roleLabels={cohortRoleLabels} relationshipPriority={canCreateMeetings ? "mentees" : "mentor"} relationshipFacts={relationshipFacts} className="cohort-member-band cohort-member-band--sand" />
            ) : null}
          </div>
        </DialogContent>
        </Dialog>
      ) : null}

      {checklistUndo ? (
        <div className={`cohort-checklist-undo-bar${featuredEvent?.canJoinVideo && isCohortEventLive(featuredEvent) && !mobileView ? " cohort-checklist-undo-bar--above-live-action" : ""}`} role="status" aria-live="polite">
          <span className="cohort-checklist-undo-bar__message">Ticked off · {checklistUndo.title}</span>
          <button type="button" onClick={handleUndoChecklistItem} className="cohort-checklist-undo-bar__action">Undo</button>
        </div>
      ) : null}

      {reportDialogOpen ? (
        <AppDialog
          onClose={handleCloseReportDialog}
          size="sm"
          className={reportSubmitted
            ? "cohort-dialog cohort-dialog--report cohort-dialog--compact-confirmation !w-[calc(100vw-2rem)] !max-w-[520px]"
            : "cohort-dialog cohort-dialog--report cohort-dialog--framed max-h-[calc(100dvh-2rem)] max-w-[560px]"}
          title={reportSubmitted ? "Report submitted" : reportDiscardConfirm ? "Keep this draft?" : <><span className="sm:hidden">Report a concern</span><span className="hidden sm:inline">Report a safety concern</span></>}
          titleClassName="text-[25px] font-normal sm:pr-10"
          description={reportSubmitted ? "An administrator will review it. You won't get a public reply, and nobody in your cohort is told you reported it." : reportDiscardConfirm ? "You've written something you haven't sent." : "Send a private note to your cohort administrators for review. Nobody else in the cohort sees it."}
          bodyClassName="space-y-4"
          isBusy={reportSubmitting}
          actions={
            <div className="w-full">
              {!reportSubmitted && !reportDiscardConfirm && !reportDescription.trim() ? <p className="mb-2 text-center text-sm text-[var(--cohort-ink-muted)] sm:text-right">Add a few words above to send.</p> : null}
              <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
                {reportSubmitted ? <Button type="button" onClick={() => { setReportDialogOpen(false); resetReportDialog() }} className="cohort-primary-button w-full sm:w-auto">Done</Button> : reportDiscardConfirm ? <><Button type="button" variant="secondary" onClick={() => setReportDiscardConfirm(false)} className="cohort-primary-button order-1 w-full sm:order-2 sm:w-auto">Keep writing</Button><Button type="button" variant="ghost" onClick={() => { setReportDialogOpen(false); resetReportDialog() }} className="cohort-secondary-button order-2 w-full sm:order-1 sm:w-auto">Discard</Button></> : <><Button type="button" variant="ghost" onClick={handleCloseReportDialog} disabled={reportSubmitting} className="cohort-secondary-button order-2 w-full sm:order-1 sm:w-auto">Cancel</Button><Button type="button" onClick={handleSubmitSafetyReport} disabled={reportSubmitting || reportDescription.trim().length === 0} className="cohort-primary-button order-1 min-h-[52px] w-full text-[17px] sm:order-2 sm:min-h-[46px] sm:w-auto sm:text-base">{reportSubmitting ? "Sending…" : "Send report"}</Button></>}
              </div>
            </div>
          }
        >
          {reportSubmitted || reportDiscardConfirm ? null : (
            <>
              <div className="flex items-start gap-3 rounded-xl border border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)] p-3.5 text-sm leading-5 text-[var(--cohort-ink)]">
                <Icon name="caution" className="mt-0.5 h-[18px] w-[18px] shrink-0" />
                <span>If someone is in immediate danger, use <a href="/help/resources" className="font-medium text-[var(--cohort-clay)] underline">Emergency resources</a>.</span>
              </div>
              <div>
                <p className="text-[13px] text-[var(--cohort-ink-muted)]">Reporting as</p>
                <p className="mt-2 text-base font-medium text-[var(--cohort-ink)]">{reporterName}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reported-user">Person involved <span className="font-normal text-[var(--cohort-ink-muted)]">— optional</span></Label>
                <Select value={reportedUserId} onValueChange={setReportedUserId} disabled={reportSubmitting}>
                  <SelectTrigger id="reported-user" className="min-h-12 rounded-xl border-[var(--cohort-rule)] bg-white text-base"><SelectValue placeholder="No specific person" /></SelectTrigger>
                  <SelectContent className="cohort-experience z-[700]">
                    <SelectItem value="none">No specific person</SelectItem>
                    {reportablePeople.map((person) => <SelectItem key={person.id} value={String(person.id)}>{person.name} ({person.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="safety-report-description-text">What happened?</Label>
                <Textarea id="safety-report-description-text" value={reportDescription} onChange={(event) => setReportDescription(event.target.value)} placeholder="Tell us what you saw or heard" rows={6} disabled={reportSubmitting} className="min-h-36 rounded-xl border-[var(--cohort-rule)] bg-white text-base" />
                <p className="text-sm leading-5 text-[var(--cohort-ink-muted)]">Say as much or as little as you like. An administrator will follow up.</p>
              </div>
              {reportError ? <p className="rounded-xl border border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)] p-3 text-sm text-[var(--cohort-clay)]">That didn&apos;t send. Your words are still here. Try again, or close and come back—the draft stays for this session.</p> : null}
            </>
          )}
        </AppDialog>
      ) : null}
      {cohortId && continuityOpen ? <ForNextTimeDialog
        open={continuityOpen}
        onOpenChange={setContinuityOpen}
        cohortId={cohortId}
        initialRelationshipId={continuityRelationshipId}
        initialMenteeId={continuityMenteeId}
        currentUserId={currentUserId}
        renderMeetingActions={(relationship, requestAction) => {
          const otherId = String(relationship.mentor.id) === currentUserId ? relationship.mentee.id : relationship.mentor.id
          const person = relationshipPeople.find(row => String(row.id) === String(otherId))
          return <RelationshipMeetingActions
            relationship={relationship}
            events={events}
            onJoinMeeting={openMeetingRoom}
            onSchedule={canCreateMeetings && String(relationship.mentor.id) === currentUserId && person ? () => requestAction(() => { setContinuityOpen(false); handleScheduleMenteeTime(person) }) : undefined}
            scheduleRequestEmail={String(relationship.mentee.id) === currentUserId ? person?.email : undefined}
            onViewProfile={() => requestAction(() => { setContinuityOpen(false); navigate(`/profile/${otherId}`) })}
          />
        }}
      /> : null}
    </div>
  )
}

export default MentorshipDashboard;
